import { getSupabaseClient } from '../../supabase/client'
import { APP_SETTINGS } from '../../config/appSettings'

// Raw note and query text never enter this dev-only log.
type Feature = 'jev_search' | 'search_comparison'
type Outcome = 'started' | 'ok' | 'skipped' | 'failed'
interface Details {
  candidateCount?: number; resultCount?: number
  elapsedMs?: number; providerMs?: number; costMicrousd?: number
  requestHash?: string
}
interface Row {
  event_id: string; owner_id: string; flow_id: string; occurred_at: string
  feature: Feature; stage: string; outcome: Outcome; reason: string | null
  note_id: string | null; note_revision_id: string | null
  request_hash: string | null
  candidate_count: number | null; result_count: number | null
  elapsed_ms: number | null; provider_ms: number | null; cost_microusd: number | null
}

const MAX_QUEUED = APP_SETTINGS.intelligenceApi.smartPilot.devLogMaxQueuedEvents
const UUID = /^[0-9a-f]{8}-[0-9a-f]{4}-4[0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i
const SHA256 = /^[a-f0-9]{64}$/
const key = (owner: string) => `minimany:dev-flow-events:v1:${owner}`
const number = (value: number | undefined, limit: number) =>
  typeof value === 'number' && Number.isFinite(value) ? Math.min(limit, Math.max(0, Math.round(value))) : null
const code = (value: string) => /^[a-z][a-z0-9_]{0,39}$/.test(value) ? value : 'unknown'
let flushing = false
const flushTimers = new Map<string, ReturnType<typeof setTimeout>>()

function queued(owner: string): Row[] {
  try {
    const value: unknown = JSON.parse(localStorage.getItem(key(owner)) ?? '[]')
    return Array.isArray(value) ? value.filter(row => row && typeof row === 'object' && row.owner_id === owner).slice(-MAX_QUEUED) as Row[] : []
  } catch { return [] }
}

function save(owner: string, rows: Row[]) {
  try { localStorage.setItem(key(owner), JSON.stringify(rows.slice(-MAX_QUEUED))) } catch { /* Diagnostics never block product use. */ }
}

export async function flushDevFlowEvents(owner: string): Promise<void> {
  if (flushing || !navigator.onLine) return
  flushing = true
  let sent = false
  try {
    const rows = queued(owner)
    if (!rows.length) return
    const client = await getSupabaseClient()
    const { data } = await client.auth.getSession()
    if (data.session?.user.id !== owner) return
    const { error } = await client.from('dev_flow_events').upsert(rows, { onConflict: 'event_id', ignoreDuplicates: true })
    if (!error) {
      const sentIds = new Set(rows.map(row => row.event_id))
      save(owner, queued(owner).filter(row => !sentIds.has(row.event_id)))
      sent = true
    }
  } catch { /* Retain the bounded queue for the next online attempt. */ }
  finally {
    flushing = false
    if (sent && queued(owner).length) queueMicrotask(() => void flushDevFlowEvents(owner))
  }
}

function scheduleFlush(owner: string) {
  if (!navigator.onLine || flushTimers.has(owner)) return
  flushTimers.set(owner, setTimeout(() => {
    flushTimers.delete(owner)
    void flushDevFlowEvents(owner)
  }, APP_SETTINGS.intelligenceApi.smartPilot.devLogFlushDelayMs))
}

export interface DevFlow {
  id: string
  identifyNote: (noteId: string, noteRevisionId?: string) => void
  event: (stage: string, outcome: Outcome, reason?: string, details?: Details) => void
}

export function createDevFlow(owner: string, feature: Feature, note?: { noteId: string; noteRevisionId?: string }): DevFlow {
  const id = crypto.randomUUID()
  let noteId = note?.noteId && UUID.test(note.noteId) ? note.noteId : null
  let noteRevisionId = noteId && note?.noteRevisionId && UUID.test(note.noteRevisionId) ? note.noteRevisionId : null
  return { id, identifyNote(nextNoteId, nextRevisionId) {
    noteId = UUID.test(nextNoteId) ? nextNoteId : null
    noteRevisionId = noteId && nextRevisionId && UUID.test(nextRevisionId) ? nextRevisionId : null
  }, event(stage, outcome, reason, details = {}) {
    const row: Row = {
      event_id: crypto.randomUUID(), owner_id: owner, flow_id: id, occurred_at: new Date().toISOString(),
      feature, stage: code(stage), outcome, reason: reason ? code(reason) : null,
      note_id: noteId, note_revision_id: noteRevisionId,
      request_hash: details.requestHash && SHA256.test(details.requestHash) ? details.requestHash : null,
      candidate_count: number(details.candidateCount, 10000),
      result_count: number(details.resultCount, 10000), elapsed_ms: number(details.elapsedMs, 3600000),
      provider_ms: number(details.providerMs, 3600000), cost_microusd: number(details.costMicrousd, 10000000),
    }
    save(owner, [...queued(owner), row])
    scheduleFlush(owner)
  } }
}

export function devFlowReason(error: unknown): string {
  if (error instanceof Error && 'code' in error && typeof error.code === 'string') return code(error.code)
  if (error instanceof DOMException && error.name === 'AbortError') return 'aborted'
  return 'unexpected_error'
}
